import * as Sentry from "@sentry/browser";
import { useEffect } from "react";
import { useIntercom } from "react-use-intercom";
import { useIntercomHmac } from "../../hooks/api/v2/intercom/useIntercomHmac";
import { useFirebaseUser } from "../../hooks/useFirebaseUser.shared";
import { useMarketStorefrontSessionTracking } from "../../hooks/useMarketStorefrontSessionTracking";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { getServiceWorker } from "../../utils/browserCapabilities";

const useGlobalErrorReporting = () => {
  useEffect(() => {
    const captureWindowError = (event: ErrorEvent) => {
      if (event.error) Sentry.captureException(event.error);
    };

    window.addEventListener("error", captureWindowError);
    return () => window.removeEventListener("error", captureWindowError);
  }, []);
};

const useFirebaseMessagingServiceWorkerUpdates = () => {
  useEffect(() => {
    const updateMessagingWorkers = async () => {
      try {
        const serviceWorker = getServiceWorker();
        if (!serviceWorker) return;
        const registrations = await serviceWorker.getRegistrations();
        await Promise.allSettled(
          registrations.map(async (registration) => {
            if (registration.active?.scriptURL.includes("firebase-messaging")) {
              await registration.update();
            }
          }),
        );
      } catch {
        // Optional maintenance must not prevent app use when browser policy blocks it.
      }
    };
    void updateMessagingWorkers();
  }, []);
};

const useIntercomIdentity = () => {
  const intercom = useIntercom();
  const intercomHmacResponse = useIntercomHmac();
  const { user } = useFirebaseUser();
  const { business } = useSellarCurrentBusiness();
  const intercomUserHmac = intercomHmacResponse.data?.hmac;

  useEffect(() => {
    if (
      import.meta.env.VITE_ENV !== "production" ||
      !intercomUserHmac ||
      !user ||
      !business
    ) {
      return;
    }

    intercom.update({
      name: user.firebaseUser.displayName ?? undefined,
      email: user.firebaseUser.email ?? undefined,
      createdAt: user.firebaseUser.metadata.creationTime ?? undefined,
      userHash: intercomUserHmac,
      customAttributes: {
        customer_type: business.Supplier ? "Supplier" : "Trade Buyer",
      },
    });
  }, [business, intercom, intercomUserHmac, user]);
};

export const AppLifecycle = () => {
  useGlobalErrorReporting();
  useFirebaseMessagingServiceWorkerUpdates();
  useIntercomIdentity();
  useMarketStorefrontSessionTracking();

  return null;
};
